import { useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import getLocalApiUrl from "pages/reports/utils/getLocalApiUrl";
import { ReportDTO } from "../models/ReportDTO";
import { getCurrentDateForInput } from "utils/date-time";

export interface EmailScheduleDetails {
  pattern: string;
  repeat?: string;
  repeatMonth?: string;
  time: string[];
  weekday?: string[];
  weekNum?: string;
  dayOfMonth?: string;
  dateFrom: string;
  dateTo?: string;
}

export interface EmailScheduleProcessMetadata {
  lastError?: string;
  lastSuccess?: string;
}

export interface EmailScheduleDTO {
  id: number;
  reportId: number;
  name: string;
  organizationsId: number;
  details: EmailScheduleDetails;
  email: string[];
  emailBody: string;
  nextSend?: string;
  body: string;
  reportFilterSetId?: number;
  enabled: boolean;
  creatorActorId?: string;
  createdAt?: string;
  updatedAt?: string;
  processState?: string;
  processStateChangeTs?: string;
  processMetadata?: EmailScheduleProcessMetadata;
  patternDetails?: EmailScheduleDetails;
}

export interface CreateScheduleRequest {
  name: string;
  email: string[];
  emailBody: string;
  details: EmailScheduleDetails;
  enabled: boolean;
  reportFilterSetId?: number;
}

export interface UpdateScheduleRequest extends CreateScheduleRequest {
  id: number;
}

export class DuplicateScheduleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DuplicateScheduleError";
  }
}

export const useEmailSchedule = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("reports");

  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<Error | null>(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<Error | null>(null);

  const [schedules, setSchedules] = useState<EmailScheduleDTO[]>([]);
  const [schedule, setSchedule] = useState<EmailScheduleDTO | null>(null);

  const createSchedule = async (
    report: ReportDTO,
    data: CreateScheduleRequest,
  ): Promise<EmailScheduleDTO | null> => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const requestData = {
        reportId: report.id,
        name: data.name,
        details: data.details,
        email: data.email,
        body: data.emailBody,
        enabled: data.enabled,
        reportFilterSetId: data.reportFilterSetId,
      };

      const response = await axios.post(
        getLocalApiUrl("/reporting/email-schedules"),
        requestData,
      );

      const createdSchedule = response.data;

      setSchedules((prev) => [...prev, createdSchedule]);
      setSchedule(createdSchedule);

      queryClient.setQueriesData({ queryKey: ["reports"] }, (oldData: any) => {
        if (!oldData) return oldData;

        if (oldData.pages) {
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              content: page.content.map((r: ReportDTO) => {
                if (r.id === report.id) {
                  return {
                    ...r,
                    emailScheduleCount: (r.emailScheduleCount || 0) + 1,
                  };
                }
                return r;
              }),
            })),
          };
        } else if (Array.isArray(oldData)) {
          return oldData.map((r: ReportDTO) => {
            if (r.id === report.id) {
              return {
                ...r,
                emailScheduleCount: (r.emailScheduleCount || 0) + 1,
              };
            }
            return r;
          });
        }

        return oldData;
      });

      return createdSchedule;
    } catch (error) {
      console.error("Error creating schedule:", error);

      if (axios.isAxiosError(error) && error.response) {
        if (
          error.response.status === 409 &&
          error.response.data?.error === "DUPLICATE_SCHEDULE"
        ) {
          const duplicateError = new DuplicateScheduleError(
            error.response.data.message ||
              t("reports:A schedule for this report already exists"),
          );
          setCreateError(duplicateError);
          throw duplicateError;
        }

        if (
          error.response.data?.error === "INVALID_REQUEST" &&
          error.response.data?.message?.includes(
            "Email schedule with the same parameters already exists",
          )
        ) {
          const duplicateError = new DuplicateScheduleError(
            error.response.data.message ||
              t("reports:A schedule with the same parameters already exists"),
          );
          setCreateError(duplicateError);
          throw duplicateError;
        }
      }

      const genericError =
        error instanceof Error ? error : new Error(t("reports:Unknown error"));
      setCreateError(genericError);
      throw genericError;
    } finally {
      setIsCreating(false);
    }
  };

  const updateSchedule = async (
    report: ReportDTO,
    scheduleId: number,
    data: UpdateScheduleRequest,
  ): Promise<EmailScheduleDTO | null> => {
    setIsUpdating(true);
    setUpdateError(null);

    try {
      const requestData = {
        id: scheduleId,
        reportId: report.id,
        name: data.name,
        details: data.details,
        email: data.email,
        body: data.emailBody,
        enabled: data.enabled,
        reportFilterSetId: data.reportFilterSetId,
      };

      const response = await axios.put(
        getLocalApiUrl(`/reporting/email-schedules/${scheduleId}`),
        requestData,
      );

      const updatedSchedule = response.data;

      setSchedules((prev) =>
        prev.map((s) => (s.id === scheduleId ? updatedSchedule : s)),
      );
      setSchedule(updatedSchedule);

      return updatedSchedule;
    } catch (error) {
      console.error("Error updating schedule:", error);
      setUpdateError(
        error instanceof Error ? error : new Error(t("reports:Unknown error")),
      );
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteSchedule = async (
    report: ReportDTO,
    scheduleId: number,
  ): Promise<boolean> => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await axios.delete(
        getLocalApiUrl(`/reporting/email-schedules/${scheduleId}`),
      );

      setSchedules((prev) => prev.filter((s) => s.id !== scheduleId));

      if (schedule?.id === scheduleId) {
        setSchedule(null);
      }

      queryClient.setQueriesData({ queryKey: ["reports"] }, (oldData: any) => {
        if (!oldData) return oldData;

        if (oldData.pages) {
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              content: page.content.map((r: ReportDTO) => {
                if (r.id === report.id && r.emailScheduleCount > 0) {
                  return { ...r, emailScheduleCount: r.emailScheduleCount - 1 };
                }
                return r;
              }),
            })),
          };
        } else if (Array.isArray(oldData)) {
          return oldData.map((r: ReportDTO) => {
            if (r.id === report.id && r.emailScheduleCount > 0) {
              return { ...r, emailScheduleCount: r.emailScheduleCount - 1 };
            }
            return r;
          });
        }

        return oldData;
      });

      return true;
    } catch (error) {
      console.error("Error deleting schedule:", error);
      setDeleteError(
        error instanceof Error ? error : new Error(t("reports:Unknown error")),
      );
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const getSchedules = async (
    report: ReportDTO,
  ): Promise<EmailScheduleDTO[]> => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await axios.get(
        getLocalApiUrl("/reporting/email-schedules"),
        {
          params: {
            reportId: report.id,
          },
        },
      );

      const fetchedSchedules = Array.isArray(response.data)
        ? response.data
        : response.data.content || [];

      setSchedules((prev) => {
        const filteredSchedules = prev.filter((s) => s.reportId !== report.id);

        return [...filteredSchedules, ...fetchedSchedules];
      });

      return fetchedSchedules;
    } catch (error) {
      console.error("Error getting schedules:", error);
      setLoadError(
        error instanceof Error ? error : new Error(t("reports:Unknown error")),
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getSchedule = async (
    scheduleId: number,
  ): Promise<EmailScheduleDTO | null> => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await axios.get(
        getLocalApiUrl(`/reporting/email-schedules/${scheduleId}`),
      );

      const fetchedSchedule = response.data;

      setSchedule(fetchedSchedule);

      setSchedules((prev) => {
        const exists = prev.some((s) => s.id === fetchedSchedule.id);
        if (!exists) {
          return [...prev, fetchedSchedule];
        }
        return prev;
      });

      return fetchedSchedule;
    } catch (error) {
      console.error("Error getting schedule:", error);
      setLoadError(
        error instanceof Error ? error : new Error(t("reports:Unknown error")),
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmail = async (
    report: ReportDTO,
    emails: string[],
    body: string,
  ): Promise<boolean> => {
    setIsSending(true);
    setSendError(null);

    try {
      const requestData = {
        reportId: report.id,
        email: emails,
        body: body,
      };

      await axios.post(getLocalApiUrl("/reporting/send-email"), requestData);

      return true;
    } catch (error) {
      console.error("Error sending email:", error);
      setSendError(
        error instanceof Error ? error : new Error(t("reports:Unknown error")),
      );
      return false;
    } finally {
      setIsSending(false);
    }
  };

  return {
    schedules,
    schedule,
    isCreating,
    isUpdating,
    isDeleting,
    isLoading,
    isSending,
    createError,
    updateError,
    deleteError,
    loadError,
    sendError,

    createSchedule,
    updateSchedule,
    deleteSchedule,
    getSchedules,
    getSchedule,
    sendEmail,
  };
};
